// src/tutoriel/TutorielBulle.jsx — CE QUE LA PERSONNE LIT, SUR L'ÉLÉMENT RÉEL.
//
// Deux modes, décidés par le provider, jamais par cette vue :
//  · pointé      — l'élément est là : on l'entoure d'une découpe et la bulle
//                  s'ancre dessus ;
//  · descriptif  — il n'est pas là (base vide, fenêtre fermée, autre écran) :
//                  même texte, centré, plus la phrase qui dit comment y
//                  arriver. On n'invente pas un élément pour avoir une flèche.
//
// La bulle ne bloque RIEN : le calque ne capte pas les clics, l'application
// reste utilisable pendant le parcours. Avancer ne dépend jamais d'une action
// dans l'app — « Suivant » suffit toujours.
import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { elementAncre } from './ancres';

const BI = ({ icon, style }) => <i className={`bi bi-${icon}`} style={style} />;

// Le seul balisage accepté dans un texte servi : **gras**. Pas de HTML —
// le texte vient du serveur, on ne lui donne pas les clés de la page.
const enGras = (texte) => String(texte || '').split(/(\*\*[^*]+\*\*)/g).map((bout, i) =>
  bout.startsWith('**') && bout.endsWith('**')
    ? <strong key={i}>{bout.slice(2, -2)}</strong>
    : <React.Fragment key={i}>{bout}</React.Fragment>);

const MARGE = 12;

export default function TutorielBulle({ tuto }) {
  const { actif, suivant, passer, precedent, pause, emmener } = tuto;
  const etape = actif?.etape;
  const bulleRef = useRef(null);
  const [boite, setBoite] = useState(null);       // rect de l'élément visé
  const [place, setPlace] = useState(null);       // position calculée de la bulle

  // Position de la découpe — suivie au scroll et au redimensionnement.
  useLayoutEffect(() => {
    if (!etape || actif.mode !== 'pointe') { setBoite(null); return; }
    const mesurer = () => {
      const el = elementAncre(etape.ancre);
      if (!el) { setBoite(null); return; }
      const r = el.getBoundingClientRect();
      setBoite({ top: r.top, left: r.left, width: r.width, height: r.height });
    };
    mesurer();
    window.addEventListener('scroll', mesurer, true);
    window.addEventListener('resize', mesurer);
    return () => { window.removeEventListener('scroll', mesurer, true); window.removeEventListener('resize', mesurer); };
  }, [etape, actif?.mode, actif?.index]);

  // Placement de la bulle : sous l'élément, au-dessus s'il déborde.
  useLayoutEffect(() => {
    if (!boite || !bulleRef.current) { setPlace(null); return; }
    const b = bulleRef.current.getBoundingClientRect();
    const dessous = boite.top + boite.height + MARGE;
    const dessus = boite.top - b.height - MARGE;
    const top = (dessous + b.height < window.innerHeight - MARGE) ? dessous
      : (dessus > MARGE ? dessus : Math.max(MARGE, window.innerHeight - b.height - MARGE));
    const left = Math.min(
      Math.max(MARGE, boite.left + boite.width / 2 - b.width / 2),
      window.innerWidth - b.width - MARGE);
    setPlace({ top, left });
  }, [boite, etape]);

  // Clavier : Échap met en pause, les flèches avancent — SAUF dans un champ
  // de saisie. L'application reste utilisable pendant le parcours : ← dans
  // une recherche doit reculer le curseur, pas le tutoriel (et surtout pas
  // marquer une étape faite).
  useEffect(() => {
    if (!etape) return;
    const dansUnChamp = (el) => !!el && (
      /^(INPUT|TEXTAREA|SELECT)$/.test(el.tagName) || el.isContentEditable);
    const surTouche = (e) => {
      if (e.key === 'Escape') { e.preventDefault(); pause(); return; }
      if (dansUnChamp(e.target)) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); suivant(); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); precedent(); }
    };
    window.addEventListener('keydown', surTouche);
    return () => window.removeEventListener('keydown', surTouche);
  }, [etape, pause, suivant, precedent]);

  useEffect(() => { bulleRef.current?.focus(); }, [actif?.index]);

  if (!etape) return null;

  const { dernier } = actif;
  const ailleurs = !actif.surLeBonEcran && !!etape.chemin;

  const corps = (
    <div ref={bulleRef} tabIndex={-1} role="dialog" aria-labelledby="tuto-titre"
      className={`tuto-bulle${actif.mode === 'pointe' ? '' : ' tuto-bulle--centre'}`}
      style={actif.mode === 'pointe' && place ? { top: `${place.top}px`, left: `${place.left}px` } : undefined}>
      <div className="tuto-bulle__kicker">
        <span>{etape.chapitre}</span>
        <span className="tuto-bulle__compte">{actif.position}/{actif.total}</span>
      </div>
      <h2 id="tuto-titre" className="tuto-bulle__titre">{etape.titre}</h2>

      {ailleurs ? (
        <p className="tuto-bulle__texte">Cette étape se passe dans <strong>{etape.ecran_libelle}</strong>.</p>
      ) : (
        <>
          <p className="tuto-bulle__texte">{enGras(etape.texte)}</p>
          {actif.mode === 'descriptif' && etape.si_absent && (
            <p className="tuto-bulle__absent">{etape.si_absent}</p>
          )}
        </>
      )}

      <div className="tuto-bulle__pied">
        <button type="button" className="tuto-bulle__lien" onClick={pause}>Mettre en pause</button>
        <span className="tuto-bulle__espace" />
        {actif.aPrecedent && (
          <button type="button" className="erp-btn erp-btn--secondary erp-btn--sm" onClick={precedent}>Précédent</button>
        )}
        <button type="button" className="tuto-bulle__lien" onClick={passer}>Passer</button>
        {ailleurs ? (
          <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={emmener}>
            M'y emmener <BI icon="arrow-right-short" />
          </button>
        ) : (
          <button type="button" className="erp-btn erp-btn--primary erp-btn--sm" onClick={suivant}>
            {dernier ? 'Terminer' : 'Suivant'}
          </button>
        )}
      </div>
    </div>
  );

  return (
    <div className="tuto-calque">
      {actif.mode === 'pointe' && boite && (
        <div className="tuto-decoupe" aria-hidden="true"
          style={{ top: `${boite.top - 4}px`, left: `${boite.left - 4}px`,
            width: `${boite.width + 8}px`, height: `${boite.height + 8}px` }} />
      )}
      {actif.mode !== 'pointe' && <div className="tuto-voile" aria-hidden="true" />}
      {corps}
    </div>
  );
}
